import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Input,
  Sheet,
  Stack,
  Table,
  Typography,
} from "@mui/joy";
import Pagination from "@mui/material/Pagination";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { Link as RouterLink } from "react-router-dom";

import { fetchAdminQuotes, searchAdminQuotes, Quote } from "../../api";
import { formatISTDate } from "../../utils/date";

const PAGE_SIZE = 20;

type Mode = "list" | "search";

export default function AdminQuotesList() {
  const [quotes, setQuotes] = React.useState<Quote[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState<Date | null>(null);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [mode, setMode] = React.useState<Mode>("list");
  const deferredQuery = React.useDeferredValue(query.trim());

  const loadQuotes = React.useCallback(
    async (searchTerm?: string, pageToLoad = 1) => {
      setLoading(true);
      try {
        if (searchTerm && searchTerm.length > 0) {
          const data = await searchAdminQuotes(searchTerm, PAGE_SIZE);
          const items = data ?? [];
          setQuotes(items);
          setTotal(items.length);
          setPage(1);
          setMode("search");
        } else {
          const { data, total: totalCount, page: currentPage } = await fetchAdminQuotes({
            page: pageToLoad,
            limit: PAGE_SIZE,
          });
          setQuotes(data ?? []);
          setTotal(totalCount ?? data.length ?? 0);
          setPage(currentPage ?? pageToLoad);
          setMode("list");
        }
        setLastUpdatedAt(new Date());
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (deferredQuery.length > 0) {
      loadQuotes(deferredQuery, 1);
    } else {
      loadQuotes(undefined, page);
    }
  }, [deferredQuery, page, loadQuotes]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    if (deferredQuery.length > 0) {
      loadQuotes(deferredQuery, 1);
    } else {
      loadQuotes(undefined, page);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const searchApplied = mode === "search" && deferredQuery.length > 0;
  const totalLabelCount = searchApplied ? quotes.length : total;
  const countLabel = searchApplied
    ? `${totalLabelCount} ${totalLabelCount === 1 ? "match" : "matches"}`
    : `${totalLabelCount} saved`;
  const totalPages = Math.max(1, Math.ceil((mode === "search" ? quotes.length : total) / PAGE_SIZE));
  const quoteSummary = React.useMemo(() => {
    const totalCount = totalLabelCount;
    const scheduled = quotes.filter((quote) => quote.date).length;
    return { total: totalCount, scheduled };
  }, [quotes, totalLabelCount]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 2.5, md: 3 },
        gap: 2.5,
        boxShadow: "sm",
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Sheet
              variant="solid"
              color="primary"
              sx={{
                width: 44,
                height: 44,
                borderRadius: "lg",
                display: "grid",
                placeItems: "center",
                boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.3)",
              }}
            >
              <FormatQuoteRoundedIcon fontSize="small" />
            </Sheet>
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Typography level="title-lg" sx={{ fontWeight: 700 }}>
                Quote library
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Gather and refine reflections that greet devotees each morning.
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="sm" variant="soft" color="primary">
              {countLabel}
            </Chip>
            <IconButton
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={handleRefresh}
              disabled={loading}
              sx={{ borderRadius: "lg" }}
            >
              <RefreshRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Sheet
          variant="soft"
          sx={{
            borderRadius: "lg",
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.level1",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ md: "center" }}
          >
            <Input
              size="sm"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by quote or author"
              startDecorator={<SearchRoundedIcon fontSize="small" />}
              sx={{
                borderRadius: "lg",
                boxShadow: "inset 0 1px 0 rgba(15,23,42,0.08)",
                maxWidth: { xs: "100%", md: 320 },
                bgcolor: "background.surface",
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip size="sm" variant="soft" color="primary">
                {quoteSummary.total} saved
              </Chip>
              <Chip size="sm" variant="soft" color="success">
                {quoteSummary.scheduled} scheduled
              </Chip>
              {lastUpdatedAt ? (
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Updated {lastUpdatedAt.toLocaleTimeString()}
                </Typography>
              ) : null}
              {isRefreshing ? (
                <Chip
                  size="sm"
                  variant="soft"
                  color="neutral"
                  startDecorator={<CircularProgress size="sm" sx={{ "--CircularProgress-size": "14px" }} />}
                >
                  Refreshing
                </Chip>
              ) : null}
            </Stack>
          </Stack>
        </Sheet>
      </Stack>

      <Sheet
        variant="outlined"
        sx={{
          borderRadius: "lg",
          borderColor: "divider",
          overflowX: "auto",
          overflowY: "hidden",
        }}
      >
        {loading ? (
          <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Gathering reflections…
            </Typography>
          </Stack>
        ) : quotes.length === 0 ? (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 8, px: 3, textAlign: "center" }}>
            <Avatar
              variant="soft"
              color="primary"
              sx={{ width: 56, height: 56, borderRadius: "lg" }}
            >
              <FormatQuoteRoundedIcon />
            </Avatar>
            <Stack spacing={0.5}>
              <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                No quotes found
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 360 }}>
                Your first meditation will greet devotees at dawn. Share a reflection to nurture hearts today.
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Box sx={{ maxHeight: { xs: 460, lg: 520 }, overflow: "auto" }}>
          <Table
              hoverRow
              stickyHeader
              stripe="odd"
              size="sm"
              borderAxis="bothBetween"
              sx={{
                minWidth: 720,
                "--TableCell-headBackground": "var(--joy-palette-background-level1)",
                "--TableCell-paddingY": "14px",
                "--TableCell-paddingX": { xs: "14px", md: "18px" },
                "& th": {
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "text.tertiary",
                  fontWeight: 700,
                },
                "& td": {
                  fontSize: "0.86rem",
                },
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: "55%" }}>Quote</th>
                  <th style={{ width: "30%" }}>Schedule</th>
                  <th style={{ width: "15%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote) => (
                  <tr key={quote.id}>
                    <td>
                      <Stack spacing={0.5}>
                        <Typography
                          level="body-sm"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {quote.text}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                          {quote.author ? `— ${quote.author}` : "Author unknown"}
                        </Typography>
                      </Stack>
                    </td>
                    <td>
                      <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        Scheduled for {formatISTDate(quote.date)}
                      </Typography>
                    </td>
                    <td>
                      <Button
                        component={RouterLink}
                        to={`/quotes/${quote.id}`}
                        size="sm"
                        variant="soft"
                        color="primary"
                        startDecorator={<VisibilityRoundedIcon />}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </Sheet>

      {mode === "list" && totalPages > 1 ? (
        <Stack direction="row" justifyContent="center">
          <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
        </Stack>
      ) : null}
    </Card>
  );
}
